import React from 'react'
import { useParams } from 'react-router'
import { useGetCustomerByID } from '../../../../../api/dealership/customers/useGetCustomerByID'
import dayjs from 'dayjs'
import { User, Mail, Phone, MapPin, Link, BadgeCheck } from 'lucide-react'
import classNames from 'classnames'
import { LoadingComponent, Pill } from '@demandlocal/components'

interface DetailSectionProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  className?: string
}

const formatDate = (date) => (date ? dayjs(date).format('MMM D, YYYY') : '-')

const formatAddress = (address) => {
  if (!address) return ''
  const parts = [
    address.address,
    address.address2,
    address.city,
    address.state,
    address.zip,
    address.country,
  ].filter(Boolean)
  return parts.join(', ')
}
const DetailSection = ({ icon, title, children, className }: DetailSectionProps) => (
  <div
    className={classNames(
      'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md',
      className,
    )}>
    <div className="p-3 sm:p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="p-1.5 sm:p-2 rounded-full bg-gray-50 text-gray-500">{icon}</div>
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{title}</h3>
        </div>
      </div>
      <div className="mt-3 sm:mt-4 border-t border-gray-100 pt-3 sm:pt-4 space-y-3">
        {children}
      </div>
    </div>
  </div>
)

const DetailRow = ({
  label,
  value,
}: {
  label: string
  value: string | number | React.ReactNode | undefined
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 py-2">
    <span className="text-sm text-gray-500">{label}</span>
    <div className="text-sm font-medium text-gray-900 break-all sm:text-right">
      {value}
    </div>
  </div>
)

const DetailBadge = ({
  children,
  variant = 'gray',
  className,
}: {
  children: React.ReactNode
  variant?: 'success' | 'warning' | 'error' | 'gray' | 'info-purple'
  className?: string
}) => {
  const colors = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    purple: 'bg-purple-100 text-purple-800',
    default: 'bg-blue-100 text-blue-800',
  }

  return <Pill type={variant}>{children}</Pill>
}

export default function Details() {
  const { customer_id } = useParams()
  const { data: customer } = useGetCustomerByID(customer_id)

  if (!customer) return <LoadingComponent title={'Loading customer'} />

  return (
    <div className="flex flex-1 flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        {/* Basic Information */}
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
            <DetailSection
              icon={<User size={18} />}
              title="Basic Information"
              className="lg:col-span-2">
              <DetailRow
                label="Name"
                value={`${customer.first_name || ''} ${customer.middle_name || ''} ${
                  customer.last_name || ''
                }`.trim()}
              />
              {customer.is_business === 1 && (
                <DetailRow label="Business Name" value={customer.business_name} />
              )}
              <DetailRow
                label="Customer Type"
                value={
                  <DetailBadge variant="info-purple">
                    {customer.customer_type}
                  </DetailBadge>
                }
              />
              <DetailRow label="Birthday" value={formatDate(customer.birth_date)} />
            </DetailSection>

            {/* System Information - Moved up on mobile */}
            <DetailSection icon={<Link size={18} />} title="System Information">
              <DetailRow
                label="Marketing Opt-Out"
                value={
                  <DetailBadge variant={customer.marketing_opt_out ? 'error' : 'success'}>
                    {customer.marketing_opt_out ? 'Yes' : 'No'}
                  </DetailBadge>
                }
              />
              <DetailRow
                label="LinkOne ID"
                value={
                  <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
                    {customer?.id}
                  </span>
                }
              />
              <DetailRow label="Created" value={formatDate(customer.created_at)} />
              <DetailRow label="Updated" value={formatDate(customer.updated_at)} />
              {customer.deleted_at && (
                <DetailRow
                  label="Deleted"
                  value={
                    <span className="text-red-600">
                      {formatDate(customer.deleted_at)}
                    </span>
                  }
                />
              )}
            </DetailSection>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <DetailSection icon={<Mail size={18} />} title="Email Addresses">
              {(customer.emails || []).map((email, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 first:border-0 py-2 gap-2">
                  <span className="text-sm text-gray-900 break-all">{email.email}</span>
                  <div className="flex gap-2">
                    {email.preferred && (
                      <DetailBadge variant="success">
                        <BadgeCheck size={12} className="mr-1" />
                        Preferred
                      </DetailBadge>
                    )}
                    {email.do_not_email && (
                      <DetailBadge variant="error">Do Not Email</DetailBadge>
                    )}
                  </div>
                </div>
              ))}
              {!customer.emails?.length && (
                <p className="text-sm text-gray-500 italic">No email addresses</p>
              )}
            </DetailSection>

            <DetailSection icon={<Phone size={18} />} title="Phone Numbers">
              {(customer.phones || []).map((phone, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 first:border-0 py-2 gap-2">
                  <span className="text-sm text-gray-900">{phone.international}</span>
                  <div className="flex flex-wrap gap-2">
                    {phone.type && <DetailBadge>{phone.type}</DetailBadge>}
                    {phone.preferred && (
                      <DetailBadge variant="success">
                        <BadgeCheck size={12} className="mr-1" />
                        Preferred
                      </DetailBadge>
                    )}
                    {phone.do_not_text && (
                      <DetailBadge variant="error">Do Not Text</DetailBadge>
                    )}
                  </div>
                </div>
              ))}
              {!customer.phones?.length && (
                <p className="text-sm text-gray-500 italic">No phone numbers</p>
              )}
            </DetailSection>
          </div>

          {/* Addresses */}
          <DetailSection icon={<MapPin size={18} />} title="Addresses">
            {(customer.addresses || []).map((address, index) => (
              <div key={index} className="border-t border-gray-100 first:border-0 py-2">
                <p className="text-sm text-gray-900 break-words">
                  {formatAddress(address)}
                </p>
              </div>
            ))}
            {!customer.addresses?.length && (
              <p className="text-sm text-gray-500 italic">No addresses</p>
            )}
          </DetailSection>
        </div>
      </div>
    </div>
  )
}
